/**
 * AlertModalProvider — centered, blocking error/info/confirm modal.
 *
 * Why this exists alongside `notifications.show()`:
 * Toasts are low-friction and great for confirmations ("Saved", "Status:
 * Activo"), but for errors that *must* be acknowledged and that the user
 * needs to read carefully — e.g. "Cannot reactivate: mnemonic JOASIL is
 * already in use by Activo agreement CIIF-009" — a centered modal with
 * a blurred overlay is more legible and harder to miss.
 *
 * Usage:
 *   const { showAlert, confirm } = useAlertModal();
 *   showAlert({ title: 'Cannot reactivate', message: '...', color: 'red' });
 *
 *   if (await confirm({ title: 'Delete?', message: '…', destructive: true }))
 *     del.mutate(id);
 *
 * The modal mirrors DirtyFormProvider's visual language — single 4px side
 * border accent, neutral background, compact size, brand sans font — so the
 * two share a coherent identity.
 */
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconAlertTriangle, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import {
  createContext, useCallback, useContext, useMemo, useRef, useState,
  type ReactNode,
} from 'react';

export type AlertColor = 'red' | 'orange' | 'blue';

export interface AlertOptions {
  title: string;
  message: string;
  color?: AlertColor;
}

export interface ConfirmOptions {
  title: string;
  message: string;
  /** Label on the confirm button. Default 'Yes'. */
  confirmLabel?: string;
  /** Label on the cancel button. Default 'No'. */
  cancelLabel?: string;
  /** Render the confirm button red and use a trash icon. Default false. */
  destructive?: boolean;
}

interface AlertCtx {
  showAlert: (opts: AlertOptions) => void;
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
}

const Ctx = createContext<AlertCtx | null>(null);

export function useAlertModal(): AlertCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAlertModal must be used inside <AlertModalProvider>');
  return c;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (b: boolean) => void;
}

export function AlertModalProvider({ children }: { children: ReactNode }) {
  const [opts, setOpts] = useState<AlertOptions | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const showAlert = useCallback((o: AlertOptions) => { setOpts(o); }, []);
  const close = useCallback(() => setOpts(null), []);

  const confirm = useCallback((o: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...o, resolve });
    });
  }, []);

  const closeConfirm = useCallback((result: boolean) => {
    setConfirmState((curr) => {
      if (curr) curr.resolve(result);
      return null;
    });
  }, []);

  const value = useMemo(() => ({ showAlert, confirm }), [showAlert, confirm]);

  const color: AlertColor = opts?.color ?? 'red';
  const accent =
    color === 'red'    ? 'var(--mantine-color-bordeaux-6)' :
    color === 'orange' ? 'var(--mantine-color-laranja-6)' :
                         'var(--mantine-color-azul-6)';
  const Icon =
    color === 'red'    ? IconAlertCircle :
    color === 'orange' ? IconAlertTriangle :
                         IconInfoCircle;

  return (
    <Ctx.Provider value={value}>
      {children}
      <Modal
        opened={!!opts}
        onClose={close}
        centered
        withCloseButton={false}
        size="sm"
        radius="md"
        overlayProps={{ blur: 2, backgroundOpacity: 0.35 }}
        title={
          <Group gap="xs" align="center">
            <Icon size={18} color={accent} />
            <Text fw={700} size="sm">{opts?.title ?? ''}</Text>
          </Group>
        }
        styles={{
          content: { borderLeft: `4px solid ${accent}`, fontFamily: 'inherit' },
          header:  { paddingBottom: 8 },
          body:    { paddingTop: 4 },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">{opts?.message ?? ''}</Text>
          <Group justify="flex-end" gap="sm">
            <Button color="azul" size="sm" onClick={close}>Got it</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Confirm modal — used by useAlertModal().confirm(). The cancel
          ('No') button is auto-focused so a hurried Enter keypress doesn't
          confirm a destructive action. */}
      <Modal
        opened={!!confirmState}
        onClose={() => closeConfirm(false)}
        centered
        withCloseButton={false}
        size="sm"
        radius="md"
        overlayProps={{ blur: 2, backgroundOpacity: 0.35 }}
        title={
          <Group gap="xs" align="center">
            {confirmState?.destructive
              ? <IconTrash size={18} color="var(--mantine-color-bordeaux-6)" />
              : <IconAlertTriangle size={18} color="var(--mantine-color-laranja-6)" />}
            <Text fw={700} size="sm">{confirmState?.title ?? ''}</Text>
          </Group>
        }
        styles={{
          content: {
            borderLeft: `4px solid ${confirmState?.destructive
              ? 'var(--mantine-color-bordeaux-6)'
              : 'var(--mantine-color-laranja-6)'}`,
            fontFamily: 'inherit',
          },
          header: { paddingBottom: 8 },
          body:   { paddingTop: 4 },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">{confirmState?.message ?? ''}</Text>
          <Group justify="flex-end" gap="sm">
            <Button
              ref={cancelBtnRef}
              variant="default"
              size="sm"
              autoFocus
              onClick={() => closeConfirm(false)}
            >
              {confirmState?.cancelLabel ?? 'No'}
            </Button>
            <Button
              color={confirmState?.destructive ? 'bordeaux' : 'azul'}
              size="sm"
              onClick={() => closeConfirm(true)}
            >
              {confirmState?.confirmLabel ?? 'Yes'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Ctx.Provider>
  );
}
